import React, { useState } from "react";

import { formatChipDate } from "../../utils/format";
import type { PromoterDisplayMode, PublicRsvpMeta, TicketTemplateConfig, TicketTemplateId } from "../../types/rsvp";
import { MobileSelect } from "../ui/MobileSelect";
import { TicketTemplatePreviewForm } from "./TicketTemplatePreviewForm";
import "./ticketTemplate.css";
import "./zoniqTicketTemplate.css";

type Props = {
  templateId: TicketTemplateId;
  config: TicketTemplateConfig;
  meta: Pick<
    PublicRsvpMeta,
    "event_name" | "event_date" | "city" | "artist" | "link_kind" | "banner_url" | "lineup"
  >;
  eyebrow?: string;
  children?: React.ReactNode;
  previewMode?: boolean;
  previewPromoters?: { id: string; name: string }[];
};

function badgeLabel(meta: Props["meta"], eyebrow?: string): string {
  if (eyebrow) return eyebrow.toUpperCase();
  return meta.link_kind === "guest_list" ? "GUEST LIST" : "RSVP";
}

export const PublicTicketTemplateShell: React.FC<Props> = ({
  templateId,
  config,
  meta,
  eyebrow,
  children,
  previewMode = false,
  previewPromoters = [],
}) => {
  const lineupBold = config.lineup_display === "bold";
  const [lineupOpen, setLineupOpen] = useState(lineupBold);
  const lineup = meta.lineup ?? [];
  const showLineup = lineup.length > 0 && config.lineup_display !== "hidden";
  const banner = config.show_banner ? meta.banner_url : null;
  const themeClass = templateId === "white" ? "zticket-page--white" : "";
  const displayDate = meta.event_date ? formatChipDate(meta.event_date) : "—";
  const venue = meta.city || meta.artist || "—";

  return (
    <div className={`zticket-page ${themeClass}`.trim()}>
      <article className="zticket-card">
        <header className="zticket-banner">
          {banner ? <img className="zticket-banner__img" src={banner} alt="" /> : null}
          <div className="zticket-banner__shade" aria-hidden="true" />
          <div className="zticket-banner__top zticket-banner__top--no-logo">
            <span className="zticket-badge">{badgeLabel(meta, eyebrow)}</span>
          </div>
          {/* The "client upload" placeholder is only useful while an admin is
             previewing/editing the template — real guests shouldn't see a
             placeholder for a banner that was never added. */}
          {!banner && config.show_banner && previewMode ? (
            <span className="zticket-banner__placeholder">BANNER 16:9 · CLIENT UPLOAD</span>
          ) : null}
          <div className="zticket-banner__foot">
            <h1 className="zticket-event-title">{meta.event_name}</h1>
            <div className="zticket-event-meta">
              <div className="zticket-event-meta__col">
                <div className="zticket-event-meta__label">DATE</div>
                <div className="zticket-event-meta__value">{displayDate}</div>
              </div>
              <div className="zticket-event-meta__col">
                <div className="zticket-event-meta__label">VENUE</div>
                <div className="zticket-event-meta__value">{venue}</div>
              </div>
            </div>
          </div>
        </header>

        <div className="zticket-perf" aria-hidden="true">
          <span className="zticket-perf__notch zticket-perf__notch--left" />
          <span className="zticket-perf__notch zticket-perf__notch--right" />
          <span className="zticket-perf__dash" />
        </div>

        <div className="zticket-body">
          {showLineup ? (
            <div className="zticket-lineup">
              <button
                type="button"
                className={`zticket-lineup-toggle${lineupBold ? " zticket-lineup-toggle--static" : ""}`}
                onClick={lineupBold ? undefined : () => setLineupOpen((v) => !v)}
                aria-expanded={lineupOpen || lineupBold}
                disabled={lineupBold}
              >
                <span className="zticket-lineup-toggle__label">LINE-UP</span>
                {lineupBold ? null : (
                  <span className="zticket-lineup-toggle__action">{lineupOpen ? "Hide" : "Show"}</span>
                )}
              </button>
              {lineupOpen || lineupBold ? (
                <div className="zticket-lineup-list">
                  {lineup.map((row) => (
                    <div key={row.name} className="zticket-lineup-item">
                      <div className="zticket-lineup-item__thumb" aria-hidden="true" />
                      <div>
                        <div className="zticket-lineup-item__name">{row.name}</div>
                        {row.role ? <div className="zticket-lineup-item__role">{row.role}</div> : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {previewMode ? (
            <TicketTemplatePreviewForm
              linkKind={meta.link_kind === "guest_list" ? "guest_list" : "rsvp"}
              config={config}
              promoters={previewPromoters}
            />
          ) : (
            children
          )}
        </div>
      </article>
    </div>
  );
};

export type PromoterFieldProps = {
  display: PromoterDisplayMode;
  promoters: { id: string; name: string }[];
  value: string;
  onChange: (promoterId: string) => void;
  required?: boolean;
};

/** @deprecated Use ZoniqTicketFormBody promoter toggle — kept for any legacy imports. */
export const PromoterField: React.FC<PromoterFieldProps> = ({
  display,
  promoters,
  value,
  onChange,
  required = true,
}) => {
  const [open, setOpen] = useState(display === "bold");
  if (display === "hidden" || promoters.length === 0) return null;

  const field = (
    <MobileSelect
      label="Promoter"
      className="zticket-select"
      value={value}
      onChange={onChange}
      options={[
        { value: "", label: "Select promoter" },
        ...promoters.map((p) => ({ value: p.id, label: p.name })),
      ]}
    />
  );

  if (display === "bold") return field;

  return (
    <>
      <button type="button" className="zticket-promo-toggle" onClick={() => setOpen((v) => !v)}>
        {open ? "Hide promoter" : "+ I have a promoter code"}
      </button>
      {open ? field : null}
    </>
  );
};
